import { cn } from '@seen/ui'
import { type JSX, memo } from 'react'
import { match } from 'ts-pattern'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import type { TimelineItem as TimelineItemType } from '#src/hooks/useTimelineItems.ts'
import { AppointmentTimelineItem } from '#src/pages/participants/timeline/AppointmentTimelineItem.tsx'
import { AssessmentItem } from '#src/pages/participants/timeline/AssessmentItem.tsx'
import { DayCenterDayNoteItem } from '#src/pages/participants/timeline/DayCenterDayNoteItem.tsx'
import { DocumentReferenceItem } from '#src/pages/participants/timeline/DocumentReferenceItem.tsx'
import { FacilityEncounterItem } from '#src/pages/participants/timeline/FacilityEncounterItem.tsx'
import { FaxItem } from '#src/pages/participants/timeline/FaxItem.tsx'
import { GroupedDocumentReferencesItem } from '#src/pages/participants/timeline/GroupedDocumentReferencesItem.tsx'
import { MedicationRequestItem } from '#src/pages/participants/timeline/MedicationRequestItem.tsx'
import { OrderItem } from '#src/pages/participants/timeline/OrderItem.tsx'
import { PhoneCallItem } from '#src/pages/participants/timeline/PhoneCallItem.tsx'
import { RecurringAppointmentTimelineItem } from '#src/pages/participants/timeline/RecurringAppointmentTimelineItem.tsx'
import { TaskTimelineItem } from '#src/pages/participants/timeline/TaskTimelineItem.tsx'
import { ValidatedToolResponseItem } from '#src/pages/participants/timeline/ValidatedToolResponseItem.tsx'
import { VisitNoteItem } from '#src/pages/participants/timeline/VisitNoteItem.tsx'

export interface TimelineItemComponentProps {
  item: TimelineItemType
}

export const TimelineItemComponent = memo(function TimelineItemComponent({
  item,
}: TimelineItemComponentProps): JSX.Element {
  return (
    <div
      className={cn(
        'isolate' // Some inner components have z-10
      )}
    >
      <TimelineItemComponentInner item={item} />
    </div>
  )
})

function TimelineItemComponentInner({
  item,
}: TimelineItemComponentProps): JSX.Element {
  return match(item)
    .with({ type: 'validatedTool' }, (item) => (
      <ValidatedToolResponseItem validatedToolResponse={item.data} />
    ))
    .with({ type: 'visitNote' }, (item) => (
      <VisitNoteItem visitNote={item.data} />
    ))
    .with({ type: 'assessment' }, (item) => (
      <AssessmentItem assessment={item.data} />
    ))
    .with({ type: 'timelineNote' }, (item) => (
      <ActivityComment
        comment={item.data}
        dateComponent={(date) => <HoverDate date={date} format="datetime" />}
      />
    ))
    .with({ type: 'phoneCall' }, (item) => (
      <PhoneCallItem phoneCall={item.data} />
    ))
    .with({ type: 'fax' }, (item) => <FaxItem fax={item.data} />)
    .with({ type: 'order' }, (item) => <OrderItem order={item.data} />)
    .with({ type: 'medicationRequest' }, (item) => (
      <MedicationRequestItem medicationRequest={item.data} />
    ))
    .with({ type: 'task' }, (item) => <TaskTimelineItem task={item.data} />)
    .with({ type: 'appointment' }, (item) => (
      <AppointmentTimelineItem appointment={item.data} />
    ))
    .with({ type: 'recurringAppointment' }, (item) => (
      <RecurringAppointmentTimelineItem appointments={item.data} />
    ))
    .with({ type: 'documentReference' }, (item) => (
      <DocumentReferenceItem documentReference={item.data} />
    ))
    .with({ type: 'groupedDocumentReferences' }, (item) => (
      <GroupedDocumentReferencesItem documentReferences={item.data} />
    ))
    .with({ type: 'dayCenterDayNote' }, (item) => (
      <DayCenterDayNoteItem dayCenterDayNote={item.data} />
    ))
    .with({ type: 'facilityEncounter' }, (item) => (
      <FacilityEncounterItem encounter={item.data} />
    ))
    .exhaustive()
}
